// src/components/draft/DraftTicker.js
// Horizontal scrolling ticker showing all draft picks with current pick centered
import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, User, Clock } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import PlayerLink from '../PlayerLink';

const DraftTicker = ({
  picks = [],
  currentPickNumber,
  onPickClick,
  userTeamId
}) => {
  const scrollRef = useRef(null);
  const currentPickRef = useRef(null);

  // Drag-to-scroll state
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Sort picks by overall_pick_number
  const sortedPicks = useMemo(() => {
    return [...picks].sort((a, b) =>
      (a.overall_pick_number || 0) - (b.overall_pick_number || 0)
    );
  }, [picks]);

  // Auto-scroll to center current pick when it changes
  useEffect(() => {
    if (currentPickRef.current && scrollRef.current) {
      const container = scrollRef.current;
      const currentPick = currentPickRef.current;

      // Calculate scroll position to center the current pick
      const containerWidth = container.offsetWidth;
      const pickLeft = currentPick.offsetLeft;
      const pickWidth = currentPick.offsetWidth;

      const scrollPosition = pickLeft - (containerWidth / 2) + (pickWidth / 2);

      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  }, [currentPickNumber]);

  // Manual scroll handlers
  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Drag-to-scroll handlers
  const handleMouseDown = useCallback((e) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
    scrollRef.current.style.cursor = 'grabbing';
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    if (scrollRef.current) {
      scrollRef.current.style.cursor = 'grab';
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // Scroll speed multiplier
    scrollRef.current.scrollLeft = scrollLeft - walk;
  }, [isDragging, startX, scrollLeft]);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      if (scrollRef.current) {
        scrollRef.current.style.cursor = 'grab';
      }
    }
  }, [isDragging]);

  if (!picks.length) {
    return null;
  }

  return (
    <div className={`${dynastyTheme.components.card.base} p-2 mb-4`}>
      <div className="flex items-center gap-2">
        {/* Left scroll button */}
        <button
          onClick={() => scroll('left')}
          className={`p-2 rounded-full ${dynastyTheme.classes.bg.darkLighter} hover:bg-gray-600 transition-colors flex-shrink-0`}
        >
          <ChevronLeft className="w-5 h-5 text-gray-400" />
        </button>

        {/* Scrollable ticker - drag to scroll */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-x-auto scrollbar-hide select-none"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
            cursor: 'grab'
          }}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <div className="flex gap-2 py-1" style={{ minWidth: 'max-content' }}>
            {sortedPicks.map((pick) => {
              const pickNumber = pick.overall_pick_number;
              const isCurrent = pickNumber === currentPickNumber;
              const isPicked = !!pick.league_player_id || !!pick.player?.info?.full_name;
              const isSkipped = !isPicked && !isCurrent && pickNumber < currentPickNumber;
              const isUserTeam = pick.current_owner_team_id === userTeamId;

              // Determine tile style based on state
              const isUserPick = isUserTeam && isPicked;

              // Build 3D style with proper depth
              let tileStyle;
              if (isCurrent) {
                tileStyle = {
                  background: 'linear-gradient(180deg, rgba(251, 191, 36, 0.5) 0%, rgba(180, 83, 9, 0.6) 100%)',
                  boxShadow: '0 6px 20px rgba(251, 191, 36, 0.4), 0 2px 4px rgba(0,0,0,0.3), inset 0 2px 0 rgba(255,255,255,0.2), inset 0 -2px 0 rgba(0,0,0,0.2)',
                  border: '1px solid rgba(251, 191, 36, 0.6)'
                };
              } else if (isUserPick) {
                // User's team picks - golden/amber theme
                tileStyle = {
                  background: 'linear-gradient(180deg, rgba(251, 191, 36, 0.3) 0%, rgba(180, 83, 9, 0.4) 100%)',
                  boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3), 0 2px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -1px 0 rgba(0,0,0,0.2)',
                  border: '1px solid rgba(251, 191, 36, 0.5)'
                };
              } else if (isPicked) {
                // Other teams' picks - teal theme
                tileStyle = {
                  background: 'linear-gradient(180deg, rgba(20, 184, 166, 0.4) 0%, rgba(15, 100, 90, 0.5) 100%)',
                  boxShadow: '0 4px 12px rgba(20, 184, 166, 0.25), 0 2px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -1px 0 rgba(0,0,0,0.2)',
                  border: '1px solid rgba(20, 184, 166, 0.5)'
                };
              } else if (isSkipped) {
                // Skipped - muted red tint
                tileStyle = {
                  background: 'linear-gradient(180deg, rgba(60, 40, 40, 0.9) 0%, rgba(30, 20, 20, 0.95) 100%)',
                  boxShadow: '0 3px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05), inset 0 -1px 0 rgba(0,0,0,0.3)',
                  border: '1px solid rgba(120, 60, 60, 0.4)',
                  opacity: 0.7
                };
              } else {
                // Unpicked - neutral with 3D depth
                tileStyle = {
                  background: 'linear-gradient(180deg, rgba(82, 82, 82, 0.9) 0%, rgba(38, 38, 38, 0.95) 100%)',
                  boxShadow: '0 3px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(0,0,0,0.3)',
                  border: '1px solid rgba(100, 100, 100, 0.4)'
                };
              }

              return (
                <div
                  key={pick.pick_id || `pick-${pickNumber}`}
                  ref={isCurrent ? currentPickRef : null}
                  onClick={() => !isDragging && onPickClick && onPickClick(pick)}
                  className={`
                    flex-shrink-0 transition-all duration-200
                    rounded-lg p-2 min-w-[100px] max-w-[120px]
                    hover:translate-y-[-1px] active:translate-y-[1px]
                    ${isCurrent ? 'scale-110 z-10' : ''}
                  `}
                  style={tileStyle}
                >
                  {/* Pick number and round */}
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-bold ${
                      isCurrent ? 'text-yellow-200' : isUserPick ? 'text-yellow-300' : isPicked ? 'text-teal-300' : 'text-gray-400'
                    }`}>
                      #{pickNumber}
                    </span>
                    <span className={`text-xs ${
                      isCurrent ? 'text-yellow-300/80' : isUserPick ? 'text-yellow-400/70' : isPicked ? 'text-teal-400/80' : 'text-gray-500'
                    }`}>
                      R{pick.round}
                    </span>
                  </div>

                  {/* Team name */}
                  <div className={`text-xs font-semibold truncate ${
                    isCurrent ? 'text-yellow-100' : isUserPick ? 'text-yellow-200' : isPicked ? 'text-white' : 'text-gray-300'
                  }`}>
                    {pick.current_owner?.team_name || pick.team_name || 'Team'}
                  </div>

                  {/* Player name or status */}
                  <div className={`text-xs truncate mt-1 ${
                    isUserPick ? 'text-yellow-300 font-medium' : isPicked ? 'text-teal-200 font-medium' : 'text-gray-500 italic'
                  }`}>
                    {isPicked ? (
                      <span className="flex items-center gap-1">
                        <User className={`w-3 h-3 flex-shrink-0 ${isUserPick ? 'text-yellow-300' : 'text-teal-300'}`} />
                        <PlayerLink mlbId={pick.player?.ids?.mlb || null} name={pick.player?.info?.full_name || 'Selected'} style={{ color: '#f9fafb' }} />
                      </span>
                    ) : isCurrent ? (
                      <span className="flex items-center gap-1 text-yellow-200">
                        <Clock className="w-3 h-3 animate-pulse" />
                        On Clock
                      </span>
                    ) : isSkipped ? (
                      <span className="text-red-400/70">Skipped</span>
                    ) : (
                      'Upcoming'
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right scroll button */}
        <button
          onClick={() => scroll('right')}
          className={`p-2 rounded-full ${dynastyTheme.classes.bg.darkLighter} hover:bg-gray-600 transition-colors flex-shrink-0`}
        >
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      {/* Hint text */}
      <div className="flex items-center justify-center gap-1 mt-2 text-xs text-gray-500">
        <span>← Drag or scroll to view all picks →</span>
      </div>
    </div>
  );
};

export default DraftTicker;
